import { useEffect, useState } from 'react'
import { getUser, isPlus, isSignedIn, onUserChange, signOut, getUsername, setUsername, usernameAvailable, guestName, NAME_RE } from '../lib/user'
import { usingEmulator } from '../lib/firebase'
import { friendly } from '../lib/errors'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { openPortal } from '../lib/billing'
import { INPUT_METHODS, getInputMethod, setInputMethod } from './inputs/registry'
import { isMuted, setMuted } from '../lib/sound'
import { TopBar, openAuth } from './TopBar'
import { BottomNav } from './BottomNav'
import { AUTH_BENEFITS } from './AuthModal'

export function Account() {
  const [, force] = useState(0)
  useEffect(() => onUserChange(() => force((x) => x + 1)), [])
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false)
  const [name, setName] = useState(''); const [nameMsg, setNameMsg] = useState('')
  const [avail, setAvail] = useState<null | boolean>(null)
  const [billing, setBilling] = useState({ hasSub: false, lifetime: false })
  const u = getUser()
  const signed = isSignedIn()
  useEffect(() => { if (signed) getUsername().then(setName).catch(() => {}) }, [signed])
  useEffect(() => {
    const u2 = getUser()
    if (!signed || !u2) { setBilling({ hasSub: false, lifetime: false }); return }
    getDoc(doc(db, 'customers', u2.uid)).then((s2) => setBilling({
      lifetime: s2.get('lifetime') === true,
      // a live/limbo subscription the player may want to manage or cancel
      hasSub: !!s2.get('stripeCustomerId') && ['active', 'trialing', 'past_due', 'unpaid'].includes(s2.get('subscriptionStatus')),
    })).catch(() => setBilling({ hasSub: false, lifetime: false }))
  }, [signed])

  // debounced 1-read availability check against the usernames index
  useEffect(() => {
    setAvail(null)
    const n = name.trim().replace(/^@+/, '')
    if (!signed || !NAME_RE.test(n)) return
    const t = setTimeout(() => { usernameAvailable(n).then(setAvail).catch(() => {}) }, 400)
    return () => clearTimeout(t)
  }, [name, signed])

  const run = (f: () => Promise<void>) => async () => {
    setBusy(true); setErr('')
    try { await f() } catch (e) { setErr(friendly(e)) } finally { setBusy(false) }
  }

  return (
    <div className="screen">
      <TopBar />
      <h1 style={{ fontSize: 28, margin: '18px 0 4px' }}>{signed ? 'Your profile' : 'Account'}</h1>
      {!signed ? (
        <>
          <div className="card"><h3>Playing as</h3><div className="stat">{guestName()}</div><div className="tiny">a guest on this device — your progress lives in this browser only</div></div>
          <ul className="benefits card-benefits">{AUTH_BENEFITS.map(([e, t]) => <li key={t}><span>{e}</span>{t}</li>)}</ul>
          <div className="actions"><button className="btn block" onClick={openAuth}>Sign up / Login</button></div>
        </>
      ) : (
        <>
          <div className="card"><h3>Signed in</h3><div className="stat">{name || u!.displayName || u!.email || 'player'}</div><div className="tiny">{u!.email ?? ''}</div></div>
          <div className="card">
            <h3>Username</h3>
            <input className="input" maxLength={20} placeholder="3–20 chars: a–z, 0–9, - and _"
              value={name} onChange={(e) => { setName(e.target.value); setNameMsg('') }} />
            {avail === false && <div className="tiny" style={{ color: '#FF5C5C', marginBottom: 6 }}>taken — try another</div>}
            {avail === true && <div className="tiny" style={{ color: 'var(--pop)', marginBottom: 6 }}>available ✔</div>}
            <button className="btn ghost block" disabled={busy || !name.trim() || avail === false}
              onClick={run(async () => { await setUsername(name); setNameMsg('Saved ✔') })}>Save username</button>
            {nameMsg && <div className="tiny" style={{ color: 'var(--pop)', marginTop: 6 }}>{nameMsg}</div>}
          </div>
          {isPlus() && (
            <div className="card"><h3>Your Plus games</h3>
              <a className="btn block" href="#/practice" style={{ marginBottom: 8 }}>🎲 Practice — random 5, unlimited</a>
              <a className="btn ghost block" href="#/ext">✦ Today's extended pack</a>
            </div>
          )}
          <div className="card"><h3>Way Off Plus</h3>
            <div className="stat">{billing.lifetime ? <b>Lifetime ✦</b> : isPlus() ? <b>active ✦</b> : 'not yet'}</div>
            {billing.lifetime && <div className="tiny" style={{ marginTop: 4 }}>Yours forever — nothing to renew, nothing to cancel.</div>}
            {isPlus()
              ? (billing.hasSub
                ? <>
                    <button className="btn ghost block" style={{ marginTop: 10 }} disabled={busy} onClick={run(openPortal)}>Manage subscription</button>
                    {billing.lifetime && <div className="tiny" style={{ marginTop: 6 }}>You also have a monthly subscription — Lifetime already covers you, so feel free to cancel it.</div>}
                  </>
                : !billing.lifetime && <div className="tiny" style={{ marginTop: 8 }}>Your Plus was granted directly — nothing to manage here.</div>)
              : <a className="btn block" style={{ marginTop: 10 }} href="#/plus">See what's in Plus</a>}</div>
          <div className="actions"><button className="btn ghost block" disabled={busy} onClick={run(signOut)}>Sign out</button></div>
        </>
      )}
      {err && <p className="tiny" style={{ color: '#FF5C5C' }}>{err}</p>}
      <div className="card">
        <h3>Magnitude input</h3>
        {INPUT_METHODS.map((m) => (
          <label key={m.id} className="radio-row">
            <input type="radio" name="input-method" checked={getInputMethod() === m.id} onChange={() => { setInputMethod(m.id); force((x) => x + 1) }} />
            <span><b>{m.name}</b><div className="tiny">{m.desc}</div></span>
          </label>
        ))}
      </div>
      <div className="card">
        <h3>Sound</h3>
        <label className="radio-row">
          <input type="checkbox" checked={!isMuted()} onChange={(e) => { setMuted(!e.target.checked); force((x) => x + 1) }} />
          <span><b>Dial ticks</b><div className="tiny">the little click at each notch</div></span>
        </label>
      </div>
      {usingEmulator && (
        <button className="btn ghost block" style={{ marginTop: 20 }} onClick={() => { localStorage.setItem('wayoff.devPremium', localStorage.getItem('wayoff.devPremium') === '1' ? '0' : '1'); force((x) => x + 1) }}>
          DEV: toggle premium ({isPlus() ? 'on' : 'off'})
        </button>
      )}
      <BottomNav />
    </div>
  )
}
